import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Plug, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const NETWORKS = [
  {
    key: "cj",
    name: "CJ Affiliate",
    blurb: "Commission Junction — premium retailers (Lowe's, Office Depot, GoPro, etc.).",
    color: "emerald",
    docs: "https://developers.cj.com/",
  },
  {
    key: "rakuten",
    name: "Rakuten Advertising",
    blurb: "Walmart, Macy's, Best Buy, ULTA, Hot Topic, JCPenney — strong US retail catalog.",
    color: "rose",
    docs: "https://developers.rakutenadvertising.com/",
  },
  {
    key: "impact",
    name: "Impact.com",
    blurb: "Performance partnerships — Target, Adidas, Airbnb, Ticketmaster.",
    color: "sky",
    docs: "https://developer.impact.com/",
  },
  {
    key: "shareasale",
    name: "ShareASale (Awin)",
    blurb: "21,000+ smaller merchants — Reebok, NordVPN, Wayfair, Bed Bath & Beyond.",
    color: "amber",
    docs: "https://developers.shareasale.com/",
  },
];

export default function AffiliateNetworks() {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(null);
  const [results, setResults] = useState({});
  const [advertisers, setAdvertisers] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/affiliates/status");
    setStatus(data);
  };
  useEffect(() => { load(); }, []);

  const testCj = async () => {
    setTesting("cj");
    try {
      const { data } = await api.get("/admin/affiliates/cj/test");
      setResults((r) => ({ ...r, cj: data }));
      if (data.ok) toast.success("CJ credentials valid!");
      else toast.error(`CJ: ${data.message || "Failed"}`);
    } catch (e) {
      toast.error("Couldn't reach CJ");
    } finally {
      setTesting(null);
    }
  };

  const testRakuten = async () => {
    setTesting("rakuten");
    try {
      const { data } = await api.get("/admin/affiliates/rakuten/test");
      setResults((r) => ({ ...r, rakuten: data }));
      if (data.ok) toast.success("Rakuten credentials valid!");
      else toast.error(`Rakuten: ${data.message || "Failed"}`);
    } catch (e) {
      toast.error("Couldn't reach Rakuten");
    } finally {
      setTesting(null);
    }
  };

  const refreshRakutenToken = async () => {
    setTesting("rakuten-refresh");
    try {
      const { data } = await api.post("/admin/affiliates/rakuten/refresh-token");
      if (data.ok) {
        toast.success(`New token minted (expires in ${data.expires_in}s). Paste into RAKUTEN_ACCESS_TOKEN in /app/backend/.env.`);
        setResults((r) => ({ ...r, rakuten: { ok: true, message: `New token: ${data.access_token?.slice(0, 24)}…` } }));
      } else toast.error(`Refresh failed: ${data.message}`);
    } catch (e) {
      toast.error("Couldn't refresh token");
    } finally {
      setTesting(null);
    }
  };

  const fetchRakutenAdvertisers = async () => {
    setTesting("rakuten-import");
    try {
      const { data } = await api.get("/admin/affiliates/rakuten/advertisers");
      setAdvertisers({ ...data, network: "rakuten" });
      toast.success(`Fetched ${data.count} advertisers from Rakuten`);
    } catch (e) {
      toast.error("Couldn't fetch advertisers");
    } finally {
      setTesting(null);
    }
  };

  const importAdvertisers = async () => {
    setTesting("cj-import");
    try {
      const { data } = await api.get("/admin/affiliates/cj/advertisers");
      setAdvertisers(data);
      toast.success(`Fetched ${data.count} advertisers from CJ`);
    } catch (e) {
      toast.error("Couldn't fetch advertisers");
    } finally {
      setTesting(null);
    }
  };

  const syncCommissions = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/affiliates/cj/sync-commissions?days=7");
      toast.success(`Synced ${data.count} commissions from last 7 days`);
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="adm-affiliates-page">
      <PageHeader
        eyebrow="Integrations"
        title="Affiliate networks"
        subtitle="Connect your publisher accounts. Cashback transactions sync automatically once a merchant confirms a sale."
      />

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {NETWORKS.map((n) => {
          const s = status?.[n.key];
          const configured = s?.configured;
          return (
            <Card key={n.key} className="border-gray-200 shadow-none rounded-2xl" data-testid={`net-${n.key}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${
                    n.color === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
                    n.color === "rose" ? "bg-rose-50 text-rose-700 border-rose-100" :
                    n.color === "sky" ? "bg-sky-50 text-sky-700 border-sky-100" :
                    "bg-amber-50 text-amber-700 border-amber-100"
                  }`}>
                    <Plug className="w-5 h-5" />
                  </div>
                  {configured ? (
                    <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Configured
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-gray-300 text-gray-500">
                      <XCircle className="w-3 h-3 mr-1" /> Not connected
                    </Badge>
                  )}
                </div>
                <h3 className="font-display font-bold text-lg">{n.name}</h3>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">{n.blurb}</p>
                {configured && (
                  <div className="mt-3 text-xs text-gray-500 font-mono bg-gray-50 px-3 py-2 rounded-lg">
                    {n.key === "cj" && `CID: ${s.cid}`}
                    {n.key === "impact" && `SID: ${s.sid}`}
                    {n.key === "shareasale" && `Affiliate ID: ${s.affiliate_id}`}
                  </div>
                )}
                {n.key === "cj" && configured && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" onClick={testCj} disabled={testing === "cj"} variant="outline" data-testid="cj-test">
                      {testing === "cj" ? "Testing…" : "Test credentials"}
                    </Button>
                    <Button size="sm" onClick={importAdvertisers} disabled={testing === "cj-import"} variant="outline" data-testid="cj-import">
                      {testing === "cj-import" ? "Importing…" : "Fetch advertisers"}
                    </Button>
                    <Button size="sm" onClick={syncCommissions} disabled={syncing} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="cj-sync">
                      <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? "animate-spin" : ""}`} /> Sync 7d
                    </Button>
                  </div>
                )}
                {!configured && (
                  <a href={n.docs} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 hover:underline mt-3 inline-block">
                    View docs →
                  </a>
                )}
                {results[n.key] && (
                  <div className={`mt-3 text-xs rounded-lg p-2 ${results[n.key].ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
                    {results[n.key].message}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {advertisers && (
        <Card className="border-gray-200 shadow-none rounded-2xl mb-6">
          <CardContent className="p-6">
            <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-700" /> CJ joined advertisers ({advertisers.count})
            </h3>
            {advertisers.count === 0 ? (
              <p className="text-sm text-gray-600">
                No joined advertisers yet. Apply to merchants inside the CJ dashboard (login at cj.com → Advertisers → Joined),
                wait for approval, then click "Fetch advertisers" again.
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {advertisers.advertisers.slice(0, 24).map((a) => (
                  <div key={a.advertiserId} className="border border-gray-200 rounded-xl p-3 text-sm">
                    <div className="font-semibold">{a.advertiserName}</div>
                    <div className="text-xs text-gray-500 font-mono">ID: {a.advertiserId}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-gray-200 shadow-none rounded-2xl">
        <CardContent className="p-6 text-sm text-gray-700 space-y-2">
          <h3 className="font-display font-bold text-base mb-2">How to connect Impact &amp; ShareASale</h3>
          <p><strong>Impact.com</strong>: Apply at impact.com → Settings → API → generate Account SID + Auth Token. Add <code className="text-xs bg-gray-100 px-1 rounded">IMPACT_SID</code> and <code className="text-xs bg-gray-100 px-1 rounded">IMPACT_TOKEN</code> to <code className="text-xs bg-gray-100 px-1 rounded">/app/backend/.env</code>.</p>
          <p><strong>ShareASale</strong>: Apply at shareasale.com → Tools → Merchant API → API Tokens. Add <code className="text-xs bg-gray-100 px-1 rounded">SHAREASALE_TOKEN</code>, <code className="text-xs bg-gray-100 px-1 rounded">SHAREASALE_SECRET</code>, <code className="text-xs bg-gray-100 px-1 rounded">SHAREASALE_AFFILIATE_ID</code> to backend .env.</p>
          <p className="text-xs text-gray-500 mt-2"><strong>Tip:</strong> Get approved at ShareASale first (24-48hr), build a track record, then re-apply to CJ &amp; Impact with proven traffic.</p>
        </CardContent>
      </Card>
    </div>
  );
}
